import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authApiClient, ApiError } from "@/api/authApiClient";
import { AlertsWrapper } from "@/components/AlertsWrapper";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/routes/paths";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/InputOTP";

export const OTPVerification = () => {
  const [errors, setErrors] = useState<ApiError[]>([]);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const storedEmail = localStorage.getItem("otpEmail");
    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      navigate(`/${ROUTES.PUBLIC.LOGIN}`);
    }
  }, [navigate]);

  const handleOtpVerification = async (otp: string) => {
    const response = await authApiClient.verifyOtp(email, otp);
    if (response.success) {
      localStorage.removeItem("otpEmail");
      navigate("/");
    } else {
      setErrors(response.errors);
    }
  };

  return (
    <main role="main">
      <h2 className="mb-3 text-2xl font-medium text-foreground">
        {t("login.otpSubtitle")}
      </h2>
      <p className="mb-6 text-sm text-muted-foreground">
        {t("login.otpInstructions")}
      </p>
      <AlertsWrapper errors={errors} messages={[]}>
        <OtpForm handleOtpVerification={handleOtpVerification} />
      </AlertsWrapper>
    </main>
  );
};

interface OtpFields {
  otp: string;
}

function OtpForm({
  handleOtpVerification,
}: {
  handleOtpVerification: (otp: string) => Promise<void>;
}) {
  const [disabled, setDisabled] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = useForm<OtpFields>({ disabled });
  const { t } = useTranslation();

  const otpValue = watch("otp");

  const onSubmit: SubmitHandler<OtpFields> = async (fields: OtpFields) => {
    const { otp } = fields;
    setDisabled(true);
    await handleOtpVerification(otp);
    setDisabled(false);
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-foreground">
            {t("login.otpLabel")}
          </label>
          <Controller
            name="otp"
            control={control}
            rules={{
              required: true,
              minLength: {
                value: 6,
                message: t("login.otpError"),
              },
            }}
            render={({ field }) => (
              <InputOTP
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                inputMode="numeric"
                value={field.value || ""}
                onChange={field.onChange}
                data-testid="otp-input"
                disabled={disabled}
                className="[&_[data-slot=input-otp-slot]]:border-slate-600"
              >
                <InputOTPGroup className="flex w-full">
                  <InputOTPSlot index={0} className="h-14 flex-1 text-2xl" />
                  <InputOTPSlot index={1} className="h-14 flex-1 text-2xl" />
                  <InputOTPSlot index={2} className="h-14 flex-1 text-2xl" />
                  <InputOTPSlot index={3} className="h-14 flex-1 text-2xl" />
                  <InputOTPSlot index={4} className="h-14 flex-1 text-2xl" />
                  <InputOTPSlot index={5} className="h-14 flex-1 text-2xl" />
                </InputOTPGroup>
              </InputOTP>
            )}
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting || !otpValue || otpValue.length !== 6}
          className="flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? t("login.loading") : t("login.signIn")}
        </button>
      </form>
    </div>
  );
}
